import { ReactComponent as InfoIcon } from '@/assets/icon_info.svg';
import { ReactComponent as DocumentacaoIcon } from '@/assets/icons/referral.svg';
import React from 'react';
import { FiMessageCircle, FiCopy } from 'react-icons/fi';

import { ProtocoloExecutado } from '@/models/ProtocoloExecutado';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import PrescricoesButton from '@/features/prescricao-livre/components/PrescricoesButton';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';
import { useFeatures } from '@/features/features/providers/FeaturesProvider';

import ButtonSmall from '@/components/ButtonSmall';
import Tooltip from '@/components/Tooltip';
import { copyTextToClipboard } from '@/utils/clipboard';

import PatientSection from '@/features/enterprise/components/DadosAtendimento/PatientSection';
import { usePatientDisplay } from '@/features/enterprise/hooks/useEncounter';
import { useHelpCenterContext } from '@/features/helpcenter/contexts/HelpCenterContext';
import AutoriaProtocolo from '../AutoriaProtocolo';
import {
  Container,
  ExecucaoInfo,
  HeaderActions,
  HeaderColumn,
  HeaderRow,
  HeaderSeparator,
  Info,
} from './styles';

type Props = { protocolo: ProtocoloExecutado };
type DetailsProps = {
  autoria: ProtocoloExecutado['autoria'];
  timeoutInMS?: number;
};

/**
 * TODO: Generalizar este componente
 * Comportamento semelhante ao de SecondaryMenu, com a execção que o dropdown é
 * exibido via hover ao invés de onclick
 */
const AutoriaInfoButton: React.FC<DetailsProps> = ({
  autoria,
  timeoutInMS = 300,
}) => {
  const [menuShow, setMenuShow] = React.useState(false);
  const [displayTimeout, setDisplayTimeout] = React.useState<NodeJS.Timeout>();

  React.useEffect(() => {
    return () => clearTimeout(displayTimeout);
  }, [displayTimeout]);

  return (
    autoria && (
      <Info
        onMouseEnter={() => {
          clearTimeout(displayTimeout);
          setMenuShow(true);
        }}
        onMouseLeave={() => {
          clearTimeout(displayTimeout);
          setDisplayTimeout(setTimeout(() => setMenuShow(false), timeoutInMS));
        }}
      >
        <ButtonSmall>
          <InfoIcon />
        </ButtonSmall>
        {menuShow && <AutoriaProtocolo autoria={autoria} />}
      </Info>
    )
  );
};

const HeaderTitleProtocoloExecutado: React.FC<Props> = ({
  protocolo: { nome, autoria, numero_execucao, documentacao_url },
}) => {
  const copyTooltipDefaultTitle = 'Copiar número da execução';
  const copyTooltipSuccessTitle = 'Copiado!';
  const copyFeedbackTimeoutInMS = 2000;

  const { user } = useAuth();
  const [{ mode }] = useEnterpriseContext();
  const { hasFeature } = useFeatures();
  const {
    actions: { dispatchOpenModal },
  } = useHelpCenterContext();

  // Empresas com a feature "portal.acesso" usam a experiência do portal
  // pessoal, mesmo sendo usuários b2b.
  const isPortalUser = mode === 'personal' || hasFeature('portal.acesso');

  const {
    hasMinimalDisplayProperties: canDisplayPatient,
  } = usePatientDisplay();

  const contatoEmail = React.useMemo(() => {
    if (user && user.empresa) {
      return user.empresa.email_contato;
    }

    return null;
  }, [user]);

  const onButtonSupportClick = React.useCallback(() => {
    dispatchOpenModal(0);
  }, [dispatchOpenModal]);

  const handleViewDocumentacao = React.useCallback(() => {
    if (documentacao_url) {
      window.open(documentacao_url, '_blank');
    }
  }, [documentacao_url]);

  const [titleCopyButton, setTitleCopyButton] = React.useState(
    copyTooltipDefaultTitle,
  );
  const [isCopyTooltipHovered, setIsCopyTooltipHovered] = React.useState(false);
  const [isCopyFeedbackVisible, setIsCopyFeedbackVisible] = React.useState(
    false,
  );
  const copyFeedbackTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  const clearCopyFeedbackTimeout = React.useCallback(() => {
    clearTimeout(copyFeedbackTimeoutRef.current);
  }, []);

  React.useEffect(() => {
    return clearCopyFeedbackTimeout;
  }, [clearCopyFeedbackTimeout]);

  const changeTitleCopyButton = React.useCallback(() => {
    setTitleCopyButton(copyTooltipSuccessTitle);
    setIsCopyFeedbackVisible(true);
    clearCopyFeedbackTimeout();

    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setTitleCopyButton(copyTooltipDefaultTitle);
      setIsCopyFeedbackVisible(false);
    }, copyFeedbackTimeoutInMS);
  }, [
    clearCopyFeedbackTimeout,
    copyFeedbackTimeoutInMS,
    copyTooltipDefaultTitle,
    copyTooltipSuccessTitle,
  ]);

  const onCopyExecucaoId = React.useCallback(async () => {
    await copyTextToClipboard(String(numero_execucao));
    changeTitleCopyButton();
  }, [numero_execucao, changeTitleCopyButton]);

  const isCopyTooltipVisible = isCopyTooltipHovered || isCopyFeedbackVisible;

  return (
    <Container>
      <HeaderColumn>
        <HeaderRow>
          <h1>{nome}</h1>
          {autoria && <AutoriaInfoButton autoria={autoria} />}
        </HeaderRow>
        <ExecucaoInfo>
          <span>Nº: {numero_execucao}</span>
          <Tooltip title={titleCopyButton} visible={isCopyTooltipVisible}>
            <button
              type="button"
              onClick={onCopyExecucaoId}
              onMouseEnter={() => setIsCopyTooltipHovered(true)}
              onMouseLeave={() => setIsCopyTooltipHovered(false)}
            >
              <FiCopy size={16} />
            </button>
          </Tooltip>
        </ExecucaoInfo>
      </HeaderColumn>

      {canDisplayPatient && (
        <>
          <HeaderSeparator />
          <HeaderColumn>
            <PatientSection mode="clean" />
          </HeaderColumn>
        </>
      )}

      <HeaderActions>
        {isPortalUser && (
          <ButtonSmall
            disabled={!documentacao_url}
            onClick={handleViewDocumentacao}
            style={{
              padding: '8px 24px',
              border: '1px solid #D9DBE3',
              borderRadius: '32px',
            }}
          >
            <DocumentacaoIcon />
            Ver documentação
          </ButtonSmall>
        )}

        {mode === 'integrated' && !!contatoEmail && (
          <ButtonSmall onClick={onButtonSupportClick}>
            <FiMessageCircle size={22} />
            Ajuda
          </ButtonSmall>
        )}

        <PrescricoesButton />
      </HeaderActions>
    </Container>
  );
};

export default HeaderTitleProtocoloExecutado;
